import React, { useCallback, useRef, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstTable from "../../components/DstTable/DstTable";
import { useSession } from "../../contexts/SessionContext";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { InvitationImportRow } from "./Invitations.d";
import { parseCsvFile, submitInvitationsImport } from "./Invitations.function";
import "./Invitations.scss";
import InvitationModal from "./components/InvitationModal/InvitationModal";

const Invitations = () => {
  const { language, showAlert, popAlert } = useSession();
  const [csvData, setCsvData] = useState<InvitationImportRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [showSingleInvite, setShowSingleInvite] = useState(false);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingCell, setEditingCell] = useState<{
    rowIndex: number;
    columnKey: string;
  } | null>(null);
  const navigate = useNavigate();

  const _onCellChange = (rowIndex: number, columnKey: string, value: string) => {
    const newData = [...csvData];

    newData[rowIndex] = {
      ...newData[rowIndex],
      [columnKey]: value,
    };

    setCsvData(newData);
    setEditingCell(null);
  };

  const _toggleBoolean = (rowIndex: number, columnKey: keyof InvitationImportRow) => {
    const newData = [...csvData];

    newData[rowIndex] = {
      ...newData[rowIndex],
      [columnKey]: newData[rowIndex][columnKey] === "1" ? "0" : "1",
    };

    setCsvData(newData);
  };

  const onFileUpload = useCallback(
    (file: File) => {
      parseCsvFile(file, setCsvData, language);
    },
    [language]
  );

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const file = event.dataTransfer?.files[0];
      if (file) {
        onFileUpload(file);
      }
    },
    [onFileUpload]
  );

  const onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onFileUpload(file);
    }
  };

  const columns = [
    {
      name: translate(language, "PAGES.INVITATIONS.TABLE_HEADER.FIRST_NAME"),
      key: "first_name",
      sortable: true,
      render: (row: InvitationImportRow) =>
        editingCell?.rowIndex === row._index && editingCell?.columnKey === "first_name" ? (
          <div className="">
            <input
              type="text"
              defaultValue={row.first_name}
              onBlur={(event) => _onCellChange(row._index!, "first_name", event.target.value)}
            />
          </div>
        ) : (
          <div
            className="d-flex align-items-center justify-content-center gap-2 text-center clickable"
            onClick={() => setEditingCell({ rowIndex: row._index!, columnKey: "first_name" })}
          >
            {row.first_name}
            <span className="dst-icon-edit-02 clickable dropdown-icon" />
          </div>
        ),
    },
    {
      name: translate(language, "PAGES.INVITATIONS.TABLE_HEADER.LAST_NAME"),
      key: "last_name",
      sortable: true,
      render: (row: InvitationImportRow) =>
        editingCell?.rowIndex === row._index && editingCell?.columnKey === "last_name" ? (
          <div>
            <input
              type="text"
              defaultValue={row.last_name}
              onBlur={(event) => _onCellChange(row._index!, "last_name", event.target.value)}
            />
          </div>
        ) : (
          <div
            className="d-flex align-items-center justify-content-center gap-2 text-center clickable"
            onClick={() => setEditingCell({ rowIndex: row._index!, columnKey: "last_name" })}
          >
            {row.last_name}
            <span className="dst-icon-edit-02 clickable dropdown-icon" />
          </div>
        ),
    },
    {
      name: translate(language, "PAGES.INVITATIONS.TABLE_HEADER.EMAIL"),
      key: "email",
      sortable: true,
      render: (row: InvitationImportRow) =>
        editingCell?.rowIndex === row._index && editingCell?.columnKey === "email" ? (
          <div>
            <input
              type="email"
              defaultValue={row.email}
              onBlur={(event) => _onCellChange(row._index!, "email", event.target.value)}
            />
          </div>
        ) : (
          <div
            className="d-flex align-items-center justify-content-center gap-2 text-center clickable"
            onClick={() => setEditingCell({ rowIndex: row._index!, columnKey: "email" })}
          >
            {row.email}
            <span className="dst-icon-edit-02 clickable dropdown-icon" />
          </div>
        ),
    },
    {
      name: translate(language, "PAGES.INVITATIONS.TABLE_HEADER.LANGUAGE"),
      key: "language",
      sortable: true,
      render: (row: InvitationImportRow) =>
        editingCell?.rowIndex === row._index && editingCell?.columnKey === "language" ? (
          <div>
            <input
              type="text"
              defaultValue={row.language}
              onBlur={(event) => _onCellChange(row._index!, "language", event.target.value)}
            />
          </div>
        ) : (
          <div
            className="d-flex align-items-center justify-content-center gap-2 text-center clickable"
            onClick={() => setEditingCell({ rowIndex: row._index!, columnKey: "language" })}
          >
            {row.language}
            <span className="dst-icon-edit-02 clickable dropdown-icon" />
          </div>
        ),
    },
    {
      name: translate(language, "PAGES.INVITATIONS.TABLE_HEADER.COUNTRY"),
      key: "country",
      sortable: true,
      render: (row: InvitationImportRow) =>
        editingCell?.rowIndex === row._index && editingCell?.columnKey === "country" ? (
          <div>
            <input
              type="text"
              defaultValue={row.country}
              onBlur={(event) => _onCellChange(row._index!, "country", event.target.value)}
            />
          </div>
        ) : (
          <div
            className="d-flex align-items-center justify-content-center gap-2 text-center clickable"
            onClick={() => setEditingCell({ rowIndex: row._index!, columnKey: "country" })}
          >
            {row.country}
            <span className="dst-icon-edit-02 clickable dropdown-icon" />
          </div>
        ),
    },
    {
      name: translate(language, "PAGES.INVITATIONS.TABLE_HEADER.DT_START"),
      key: "dt_start",
      sortable: true,
      render: (row: InvitationImportRow) =>
        editingCell?.rowIndex === row._index && editingCell?.columnKey === "dt_start" ? (
          <input
            type="date"
            defaultValue={row.dt_start}
            onBlur={(event) => _onCellChange(row._index!, "dt_start", event.target.value)}
          />
        ) : (
          <div
            className="d-flex align-items-center justify-content-center gap-2 text-center clickable"
            onClick={() => setEditingCell({ rowIndex: row._index!, columnKey: "dt_start" })}
          >
            {row.dt_start}
            <span className="dst-icon-calendar-date clickable dropdown-icon" />
          </div>
        ),
    },
    {
      name: translate(language, "PAGES.INVITATIONS.TABLE_HEADER.DT_END"),
      key: "dt_end",
      sortable: true,
      render: (row: InvitationImportRow) =>
        editingCell?.rowIndex === row._index && editingCell?.columnKey === "dt_end" ? (
          <input
            type="date"
            defaultValue={row.dt_end}
            onBlur={(event) => _onCellChange(row._index!, "dt_end", event.target.value)}
          />
        ) : (
          <div
            className="d-flex align-items-center justify-content-center gap-2 text-center clickable"
            onClick={() => setEditingCell({ rowIndex: row._index!, columnKey: "dt_end" })}
          >
            {row.dt_end}
            <span className="dst-icon-calendar-date clickable dropdown-icon" />
          </div>
        ),
    },
    {
      name: translate(language, "PAGES.INVITATIONS.TABLE_HEADER.COHORT_IDS"),
      key: "cohort_ids",
      sortable: true,
      render: (row: InvitationImportRow) =>
        editingCell?.rowIndex === row._index && editingCell?.columnKey === "cohort_ids" ? (
          <div>
            <input
              type="text"
              defaultValue={row.cohort_ids}
              onBlur={(event) => _onCellChange(row._index!, "cohort_ids", event.target.value)}
            />
          </div>
        ) : (
          <div
            className="d-flex align-items-center justify-content-center gap-2 text-center clickable"
            onClick={() => setEditingCell({ rowIndex: row._index!, columnKey: "cohort_ids" })}
          >
            {row.cohort_ids}
            <span className="dst-icon-edit-02 clickable dropdown-icon" />
          </div>
        ),
    },
    {
      name: translate(language, "PAGES.INVITATIONS.TABLE_HEADER.IS_VIP"),
      key: "is_vip",
      sortable: true,
      render: (row: InvitationImportRow) => {
        const isTrue = row.is_vip === "1";

        return (
          <div className="d-flex align-items-center justify-content-center">
            <Form.Check
              className="d-flex"
              type="checkbox"
              id={`is_vip_${row._index}`}
              checked={isTrue}
              onChange={() => _toggleBoolean(row._index!, "is_vip")}
            />
          </div>
        );
      },
    },
    {
      name: translate(language, "PAGES.INVITATIONS.TABLE_HEADER.HAS_EXTRA_TIME"),
      key: "has_extra_time",
      sortable: true,
      render: (row: InvitationImportRow) => {
        const isTrue = row.has_extra_time === "1";

        return (
          <div className="d-flex align-items-center justify-content-center">
            <Form.Check
              className="d-flex"
              type="checkbox"
              id={`has_extra_time_${row._index}`}
              checked={isTrue}
              onChange={() => _toggleBoolean(row._index!, "has_extra_time")}
            />
          </div>
        );
      },
    },
    {
      name: translate(language, "PAGES.INVITATIONS.TABLE_HEADER.INVITE_TO_FORUM"),
      key: "invite_to_forum",
      sortable: true,
      render: (row: InvitationImportRow) => {
        const isTrue = row.invite_to_forum === "1";

        return (
          <div className="d-flex align-items-center justify-content-center">
            <Form.Check
              className="d-flex"
              type="checkbox"
              id={`invite_to_forum_${row._index}`}
              checked={isTrue}
              onChange={() => _toggleBoolean(row._index!, "invite_to_forum")}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="p-3">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex">
              <span
                className="dst-icon-chevron-left p-2 medium-gray-background rounded me-2"
                onClick={(event: React.MouseEvent<HTMLDivElement>) => completeNavigate(event, navigate, "/")}
              />
              <h2 className="mb-0">{translate(language, "PAGES.INVITATIONS.TITLE")}</h2>
            </div>
            <div className="d-flex gap-2">
              {csvData.length > 0 && (
                <DstButton
                  btnImageBefore={<span className="dst-icon-upload-03 me-2" />}
                  value={translate(language, "PAGES.INVITATIONS.NEW_CSV")}
                  variant="light"
                  clickFunction={() => fileInputRef.current?.click()}
                />
              )}
              <DstButton
                btnImageBefore={<span className="dst-icon-user-plus-02 me-2" />}
                value={translate(language, "PAGES.INVITATIONS.INVITE_SINGLE_USER")}
                variant="secondary"
                clickFunction={() => setShowSingleInvite(true)}
              />
              <DstButton
                btnImageBefore={<span className="dst-icon-download-02 me-2" />}
                value={translate(language, "PAGES.INVITATIONS.DOWNLOAD_TEMPLATE")}
                variant="light"
                clickFunction={() => {
                  const link = document.createElement("a");
                  link.href = "/share/invitation_template.csv";
                  link.download = "invitation_template.csv";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              />
              <DstButton
                btnImageAfter={<span className="dst-icon-arrow-right ms-2" />}
                value={translate(language, "PAGES.INVITATIONS.SUBMIT")}
                variant="primary"
                loading={submitting}
                disabled={submitting || csvData.length === 0}
                clickFunction={() =>
                  submitInvitationsImport(
                    csvData,
                    setSubmitting,
                    fileInputRef,
                    setCsvData,
                    language,
                    showAlert,
                    popAlert
                  )
                }
              />
            </div>
          </div>
        </Col>
      </Row>
      {csvData.length === 0 ? (
        <div className="d-flex flex-column gap-2 clickable">
          <DstTable data={csvData} columns={columns} itemsPerPage={20} />
          <div
            ref={dropZoneRef}
            className="border-2 border-dashed rounded-3 p-5 text-center d-flex flex-column align-items-center"
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={(e) => e.preventDefault()}
            onDrop={onDrop}
          >
            <div className="text-center s-regular d-flex align-items-center">
              {translate(language, "PAGES.INVITATIONS.DROP_HERE")}
              <DstButton
                value={translate(language, "PAGES.INVITATIONS.UPLOAD_LOCAL_FILE")}
                variant="link"
                btnClass="ps-1"
                clickFunction={() => fileInputRef.current?.click()}
              />
            </div>
          </div>
        </div>
      ) : (
        <React.Fragment>
          <DstTable
            data={csvData.map((item, index) => ({ ...item, _index: index }))}
            columns={columns}
            itemsPerPage={50}
            classTable="xs-bold vertical-align-middle"
          />
        </React.Fragment>
      )}
      <input type="file" id="fileInput" ref={fileInputRef} className="d-none" onChange={onFileInputChange} />
      <InvitationModal show={showSingleInvite} onClose={() => setShowSingleInvite(false)} />
    </div>
  );
};

export default Invitations;
